import { TestOrgNumberButton } from '../components/TestOrgNumberButton';
import { withTestOrgNumber } from '../utils/companyIdentifiers';
import { AddressAutocomplete, applySelectedAddress } from '../components/AddressAutocomplete';
import React, { useState, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DashboardLayout, Card, Button, FormInput } from '../components';
import { ProjectsListTable, type ProjectsListColumn, type ProjectsListServerQuery } from '../components/ProjectsListTable';
import { useColumnOrder } from '../hooks/useColumnOrder';
import { Breadcrumb } from '../components/Breadcrumb';
import { adminApi, type AdminListQuery } from '../services/api';
import { downloadBlob } from '../components/RegistrationReport';
import type { Company } from '../types';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

type ColumnField = 'companyNumber' | 'companyName' | 'organizationNumber' | 'phone' | 'email' | 'createdAt' | 'updatedAt' | 'verifiedCompany';

const DEFAULT_COLUMN_ORDER: ColumnField[] = [
  'companyNumber', 'companyName', 'organizationNumber', 'phone', 'email', 'createdAt', 'updatedAt', 'verifiedCompany'
];

/** The stored status code values, in the order the status filter lists them. */
const STATUS_VALUES = ['Verifierad', 'Ovärifierad', 'Blockerad'] as const;

interface CompanyFormData {
  companyName: string;
  organizationNumber: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
  vatNumber: string;
  website: string;
  companyPresentation: string;
  /** Admin can only block or lift a block; Verifierad comes from onboarding. */
  blocked: boolean;
}

const dateTime = (value: string | undefined | null) => (value ? new Date(value).getTime() : 0);

export function AdminCompanyList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [exportError, setExportError] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  // The server searches, filters, sorts and pages the list (ClickUp 869fjkyz1).
  const lastQuery = useRef<AdminListQuery>({});
  const lastPage = useRef<ProjectsListServerQuery | null>(null);
  const latestRequest = useRef(0);
  const [columnOrder, handleColumnReorder] = useColumnOrder('adminCompanyListColumns', DEFAULT_COLUMN_ORDER);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [formData, setFormData] = useState<CompanyFormData>({
    companyName: '',
    organizationNumber: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    postalCode: '',
    country: '',
    vatNumber: '',
    website: '',
    companyPresentation: '',
    blocked: false
  });
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Only the newest answer counts, so a slow earlier page cannot replace it.
  const loadPage = useCallback(async (query: ProjectsListServerQuery) => {
    const { page, pageSize, sort, filters } = query;
    const request = ++latestRequest.current;
    lastPage.current = query;
    lastQuery.current = { sort: sort.field, direction: sort.direction, filters };
    setIsLoading(true);
    try {
      const result = await adminApi.getCompanyPage({ page, pageSize, sort: sort.field, direction: sort.direction, filters });
      if (request !== latestRequest.current) return;
      setCompanies(result.data);
      setTotal(result.total);
      setLoadError(false);
    } catch (error) {
      if (request !== latestRequest.current) return;
      console.error('Failed to load companies:', error);
      setLoadError(true);
    } finally {
      if (request === latestRequest.current) setIsLoading(false);
    }
  }, []);

  // After a save the current page is fetched again.
  const loadData = async () => {
    if (lastPage.current) await loadPage(lastPage.current);
  };

  const getDisplayValue = (value: string | number | undefined | null): string => {
    if (value === undefined || value === null || String(value).trim() === '') {
      return t('adminCompanyList.notSpecified');
    }
    return String(value);
  };

  // The stored status values are code values; show them translated.
  const statusLabel = (value: string | undefined | null): string => {
    if (value === 'Verifierad') return t('adminCompanyList.status.verified');
    if (value === 'Ovärifierad') return t('adminCompanyList.status.unverified');
    if (value === 'Blockerad') return t('adminCompanyList.status.blocked');
    return getDisplayValue(value);
  };

  const formatDate = (dateStr: string | undefined | null): string => {
    if (!dateStr) return t('adminCompanyList.notSpecified');
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('sv-SE');
    } catch {
      return t('adminCompanyList.notSpecified');
    }
  };

  const statusBadge = (company: Company, className = '') => (
    <span className={`badge bg-${company.verifiedCompany === 'Verifierad' ? 'success' : 'secondary'} ${className}`.trim()}>
      {statusLabel(company.verifiedCompany)}
    </span>
  );

  const statusOptions = useMemo(
    () => STATUS_VALUES.map(value => ({ value, label: statusLabel(value) })),
    // statusLabel only depends on the language (t).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  const columns: ProjectsListColumn<Company>[] = useMemo(() => [
    { field: 'companyNumber', label: t('adminCompanyList.columns.companyNumber'), render: c => c.companyNumber },
    { field: 'companyName', label: t('adminCompanyList.columns.companyName'), render: c => getDisplayValue(c.companyName) },
    { field: 'organizationNumber', label: t('adminCompanyList.columns.organizationNumber'), render: c => getDisplayValue(c.organizationNumber) },
    { field: 'phone', label: t('adminCompanyList.columns.phone'), render: c => getDisplayValue(c.phone) },
    { field: 'email', label: t('adminCompanyList.columns.email'), render: c => getDisplayValue(c.email) },
    { field: 'createdAt', label: t('adminCompanyList.columns.createdAt'), defaultSortDirection: 'desc', sortValue: c => dateTime(c.createdAt), filterValue: c => formatDate(c.createdAt), render: c => formatDate(c.createdAt) },
    { field: 'updatedAt', label: t('adminCompanyList.columns.updatedAt'), defaultSortDirection: 'desc', sortValue: c => dateTime(c.updatedAt), filterValue: c => formatDate(c.updatedAt), render: c => formatDate(c.updatedAt) },
    {
      field: 'verifiedCompany',
      label: t('adminCompanyList.columns.verifiedCompany'),
      filter: 'select',
      selectAllLabel: t('adminCompanyList.allStatuses'),
      selectOptions: statusOptions,
      sortValue: c => statusLabel(c.verifiedCompany),
      render: c => statusBadge(c)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [statusOptions, t]);

  const initialFilters = useMemo<Record<string, string>>(() => {
    const companyNumberParam = searchParams.get('companyNumber');
    const init: Record<string, string> = {};
    if (companyNumberParam) init.companyNumber = companyNumberParam;
    return init;
  }, [searchParams]);

  // The server exports every contractor that matches the list, not just this page.
  const exportToExcel = async () => {
    setExportError(false);
    setIsExporting(true);
    try {
      const fields = new Set(columns.map(column => column.field));
      const { blob, filename } = await adminApi.exportCompanies({
        ...lastQuery.current,
        columns: columnOrder.filter(field => fields.has(field))
      });
      downloadBlob(blob, filename);
    } catch (error) {
      console.error('Failed to export contractors:', error);
      setExportError(true);
    } finally {
      setIsExporting(false);
    }
  };

  const handleRowClick = (companyId: string) => {
    setSelectedCompanyId(prev => prev === companyId ? null : companyId);
  };

  const openCompany = (company: Company) => navigate(`/admin/company-list/${company.id}`);

  const selectedCompany = companies.find(c => c.id === selectedCompanyId);

  const closeModal = () => {
    setShowModal(false);
    setEditingCompany(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePresentationChange = (value: string) => {
    setFormData(prev => ({ ...prev, companyPresentation: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCompany) return;

    setIsSaving(true);
    setMessage(null);

    try {
      // The status has its own audited endpoint; the details go through PATCH.
      // Admin only blocks or lifts a block: lifting it lets the server work out
      // Verifierad (onboarding complete) or Ej verifierad again.
      const { blocked, ...details } = formData;
      await adminApi.updateCompany(editingCompany.id, details);
      if (blocked !== (editingCompany.verifiedCompany === 'Blockerad')) {
        await adminApi.setCompanyVerification(editingCompany.id, blocked ? 'Blockerad' : 'Ovärifierad');
      }
      setMessage({ type: 'success', text: t('adminCompanyList.form.saveSuccess') });
      await loadData();
      closeModal();
    } catch (error) {
      console.error('Failed to save company:', error);
      setMessage({ type: 'error', text: t('adminCompanyList.form.saveError') });
    } finally {
      setIsSaving(false);
    }
  };

  const renderMobileCard = (company: Company, { open, selected }: { open: () => void; selected: boolean }) => (
    <div className={`card mb-2 admin-list-card ${selected ? 'border-primary' : ''}`} onClick={() => handleRowClick(company.id)} onDoubleClick={open}>
      <div className="card-body py-2 px-3">
        <div className="d-flex justify-content-between align-items-start">
          <div className="flex-grow-1 admin-list-card__body">
            <div className="d-flex align-items-center gap-2 mb-1">
              <span className="fw-semibold text-truncate">#{company.companyNumber} {company.companyName}</span>
              {statusBadge(company, 'flex-shrink-0')}
            </div>
            {company.organizationNumber && (
              <div className="text-muted small"><i className="feather-hash me-1"></i>{company.organizationNumber}</div>
            )}
            {company.email && (
              <div className="text-muted small text-truncate"><i className="feather-mail me-1"></i>{company.email}</div>
            )}
            {company.phone && (
              <div className="text-muted small"><i className="feather-phone me-1"></i>{company.phone}</div>
            )}
            <div className="text-muted small mt-1"><i className="feather-calendar me-1"></i>{formatDate(company.createdAt)}</div>
          </div>
          <div className="d-flex gap-1 flex-shrink-0 ms-2">
            <button
              type="button"
              className="btn btn-sm btn-light-primary admin-list-card__open"
              onClick={(e) => { e.stopPropagation(); open(); }}
              title={t('adminCompanyList.edit')}
              aria-label={t('adminCompanyList.edit')}
            >
              <i className="feather-arrow-right" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('adminCompanyList.title')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/admin/home' },
            { label: t('adminCompanyList.title') }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('adminCompanyList.description')}</p>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show`}>
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
        </div>
      )}

      {loadError && <div className="alert alert-danger" role="alert">{t('adminLists.loadError')}</div>}
      {exportError && <div className="alert alert-danger" role="alert">{t('adminLists.exportError')}</div>}

      <Card>
        <ProjectsListTable
          rows={companies}
          server={{ total, loading: isLoading, onQueryChange: loadPage }}
          columns={columns}
          rowKey={c => c.id}
          columnOrder={columnOrder}
          onColumnReorder={handleColumnReorder}
          reorderTitle={t('adminCompanyInvitations.dragToReorder')}
          initialSortField="companyNumber"
          initialSortDirection="asc"
          initialFilters={initialFilters}
          onOpen={openCompany}
          onSelect={c => handleRowClick(c.id)}
          selectedKey={selectedCompanyId}
          actionTitle={t('adminCompanyList.edit')}
          emptyText={t('adminCompanyList.noCompanies')}
          noResultsText={t('adminCompanyList.noResults')}
          rangeText={range => t('adminCompanyList.showingRange', range)}
          mobileFilter={{
            searchField: 'companyName',
            select: { field: 'verifiedCompany', allLabel: t('adminCompanyList.allStatuses'), options: statusOptions },
            sort: {
              label: t('projects.sort.label'),
              options: [
                { field: 'companyNumber', direction: 'asc', label: t('adminCompanyList.sort.companyNumber') },
                { field: 'companyName', direction: 'asc', label: t('adminCompanyList.sort.companyNameAZ') },
                { field: 'createdAt', direction: 'desc', label: t('adminCompanyList.sort.newest') }
              ]
            }
          }}
          renderMobileCard={renderMobileCard}
          testIds={{ table: 'admin-company-list-table', cards: 'admin-company-list-cards' }}
          toolbar={
            <>
              <Button className="admin-list-toolbar__btn" onClick={() => navigate('/admin/company-list/create')} aria-label={t('companySetup.title')}>
                <i className="feather-plus" aria-hidden="true"></i>
                <span className="d-none d-sm-inline ms-2">{t('companySetup.title')}</span>
              </Button>
              <Button
                variant="light-brand"
                className="admin-list-toolbar__btn"
                onClick={() => selectedCompany && openCompany(selectedCompany)}
                disabled={!selectedCompany}
                aria-label={t('adminCompanyList.edit')}
              >
                <i className="feather-eye" aria-hidden="true"></i>
                <span className="d-none d-sm-inline ms-2">{t('adminCompanyList.edit')}</span>
              </Button>
              <Button variant="light-brand" className="admin-list-toolbar__btn" onClick={exportToExcel} disabled={isExporting} aria-label={t('adminCompanyList.export')}>
                <i className="feather-download" aria-hidden="true"></i>
                <span className="d-none d-sm-inline ms-2">{t('adminCompanyList.export')}</span>
              </Button>
              <Button variant="light-brand" className="admin-list-toolbar__btn" onClick={() => navigate('/admin/company-registrations')} aria-label={t('adminCompanyRegistrations.title')}>
                <i className="feather-bar-chart-2" aria-hidden="true"></i>
                <span className="d-none d-sm-inline ms-2">{t('adminCompanyRegistrations.title')}</span>
              </Button>
            </>
          }
        />
      </Card>

      {/* Edit Modal */}
      {showModal && editingCompany && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflow: 'auto' }}>
          <div className="modal-dialog modal-lg" style={{ marginTop: '20px', marginBottom: '20px' }}>
            <div className="modal-content">
              <div className="modal-header" style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'white' }}>
                <h5 className="modal-title">{t('adminCompanyList.form.editTitle')}</h5>
                <button type="button" className="btn-close" onClick={closeModal}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row">
                    {/* Company Number (readonly) */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">{t('adminCompanyList.columns.companyNumber')}</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={editingCompany.companyNumber}
                        disabled
                      />
                    </div>

                    {/* Company Name */}
                    <div className="col-md-6 mb-3">
                      <FormInput
                        name="companyName"
                        label={t('adminCompanyList.columns.companyName')}
                        value={formData.companyName}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="row">
                    {/* Organization Number */}
                    <div className="col-md-6 mb-3">
                      <FormInput
                        name="organizationNumber"
                        label={t('adminCompanyList.columns.organizationNumber')}
                        value={formData.organizationNumber}
                        onChange={handleInputChange}
                      >
                        <TestOrgNumberButton onGenerated={value => { setFormData(previous => withTestOrgNumber(previous, value)); }} />
                      </FormInput>
                    </div>

                    {/* Phone */}
                    <div className="col-md-6 mb-3">
                      <FormInput
                        type="tel"
                        name="phone"
                        label={t('adminCompanyList.columns.phone')}
                        value={formData.phone}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="row">
                    {/* Email */}
                    <div className="col-md-6 mb-3">
                      <FormInput
                        name="email"
                        label={t('adminCompanyList.columns.email')}
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                      />
                    </div>

                    {/* Address */}
                    <div className="col-md-6 mb-3">
                      <AddressAutocomplete
                        name="address"
                        onAddressSelect={address => setFormData(previous => applySelectedAddress(previous, address))}
                        label={t('adminCompanyList.form.address')}
                        value={formData.address}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="row">
                    {/* City */}
                    <div className="col-md-4 mb-3">
                      <FormInput
                        name="city"
                        label={t('adminCompanyList.form.city')}
                        value={formData.city}
                        onChange={handleInputChange}
                      />
                    </div>

                    {/* Postal Code */}
                    <div className="col-md-4 mb-3">
                      <FormInput
                        name="postalCode"
                        label={t('adminCompanyList.form.postalCode')}
                        value={formData.postalCode}
                        onChange={handleInputChange}
                      />
                    </div>

                    {/* Country */}
                    <div className="col-md-4 mb-3">
                      <FormInput
                        name="country"
                        label={t('adminCompanyList.form.country')}
                        value={formData.country}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="row">
                    {/* VAT Number */}
                    <div className="col-md-6 mb-3">
                      <FormInput
                        name="vatNumber"
                        label={t('adminCompanyList.form.vatNumber')}
                        value={formData.vatNumber}
                        onChange={handleInputChange}
                      />
                    </div>

                    {/* Status */}
                    <div className="col-md-6 mb-3" data-testid="admin-company-status">
                      <span className="form-label d-block">{t('adminCompanyList.columns.verifiedCompany')}</span>
                      <div>{statusBadge(editingCompany)}</div>
                      <div className="form-text">{t('adminCompanyList.form.verifiedHelp')}</div>
                      <div className="form-check form-switch admin-company-block-toggle mt-2">
                        <input
                          id="adminCompanyBlocked"
                          type="checkbox"
                          role="switch"
                          className="form-check-input"
                          checked={formData.blocked}
                          onChange={e => setFormData(prev => ({ ...prev, blocked: e.target.checked }))}
                        />
                        <label className="form-check-label" htmlFor="adminCompanyBlocked">
                          {t('adminCompanyList.form.blocked')}
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    {/* Created At (readonly) */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">{t('adminCompanyList.columns.createdAt')}</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={formatDate(editingCompany.createdAt)}
                        disabled
                      />
                    </div>

                    {/* Updated At (readonly) */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">{t('adminCompanyList.columns.updatedAt')}</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={formatDate(editingCompany.updatedAt)}
                        disabled
                      />
                    </div>
                  </div>

                  <div className="row">
                    {/* Website */}
                    <div className="col-md-6 mb-3">
                      <FormInput
                        name="website"
                        label={t('adminCompanyList.form.website')}
                        value={formData.website}
                        onChange={handleInputChange}
                      />
                    </div>

                  </div>

                  {/* Company Presentation (Rich Editor) */}
                  <div className="mb-4">
                    <label className="form-label">{t('adminCompanyList.form.companyPresentation')}</label>
                    <ReactQuill
                      theme="snow"
                      value={formData.companyPresentation}
                      onChange={handlePresentationChange}
                      style={{ height: '150px' }}
                    />
                  </div>
                  {/* Spacer for Quill toolbar */}
                  <div style={{ height: '45px' }}></div>
                </div>
                <div className="modal-footer" style={{ position: 'sticky', bottom: 0, zIndex: 10, backgroundColor: 'white', borderTop: '1px solid #dee2e6' }}>
                  <Button type="button" variant="secondary" onClick={closeModal}>
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" isLoading={isSaving}>
                    {t('common.save')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
